import { useState, useEffect, useCallback } from "react";
import { API } from "../contexts/AuthContext";

/**
 * Hook to cache a trip's API response for offline access.
 * Works directly with the Cache Storage API (same store as the SW).
 */
export function useOfflineCache(tripId) {
  const [cached, setCached] = useState(false);
  const [caching, setCaching] = useState(false);

  // Check if this trip is already in any SW cache
  const checkCached = useCallback(async () => {
    if (!("caches" in window) || !tripId) return;
    const url = `${API}/trips/${tripId}`;
    const match = await window.caches.match(url);
    setCached(!!match);
  }, [tripId]);

  useEffect(() => { checkCached(); }, [checkCached]);

  // Fetch the trip data and put it in the SW API cache
  const cacheTrip = useCallback(async () => {
    if (!("caches" in window) || caching) return false;
    setCaching(true);
    try {
      const url = `${API}/trips/${tripId}`;

      // Find the versioned API cache (robust to SW version bumps)
      const keys = await window.caches.keys();
      const apiCacheName = keys.find((k) => k.includes("-api")) || "voyage-r-v5-api";
      const cache = await window.caches.open(apiCacheName);

      const response = await fetch(url);
      if (response.ok) {
        await cache.put(url, response.clone());
        setCached(true);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      setCaching(false);
    }
  }, [tripId, caching]);

  return { cached, caching, cacheTrip, recheckCache: checkCached };
}
